import request, { authOptions } from 'api/requestUtils'

const METASEARCH_VOUCHER_API_TIMEOUT_MS = 5000

export interface CreateMetasearchDiscountVoucherInput {
  accountType?: string
  /**
   * Google's signed discount token. Optional since GHA-585: Google leaves RATE-PLAN-ID empty on
   * a sizeable share of clicks, and `rateRuleId` carries the fallback instead.
   */
  token?: string
  propertyId: string
  checkIn: string
  checkOut: string
  occupancy: Array<string>
  region: string
  isCugRate: boolean
  mx?: string
  /**
   * Google's raw stored rate-rule ID, forwarded verbatim so voucher entitlement resolves against
   * the same rule Google selected. Optional — absent for non-country / already-cached traffic.
   */
  rateRuleId?: string
  bedbankSessionId?: string
  /**
   * Google's displayed total from the landing URL (`priceTotal`) and the currency it is quoted
   * in. Sent together or not at all: it stands in for the price a signed token would have baked,
   * so the resolver's price buffer can still hold the number Google showed (GHA-585). A price
   * without its currency cannot be compared against the charged one, so it buys nothing.
   */
  advertisedPrice?: number
  advertisedCurrency?: string
}

export interface CreateMetasearchDiscountVoucherResponse {
  voucherId?: string
  /**
   * GHA-404: present, and true, only when the service flagged the shopper (hotel, uncertain or a
   * blocked identity) and withheld the voucher. It is an instruction, not the verdict: price this
   * session at the public rate. Absent on an ordinary no-discount `{}`.
   */
  applyPublicRate?: true
}

export function createMetasearchDiscountVoucher(
  input: CreateMetasearchDiscountVoucherInput,
): Promise<CreateMetasearchDiscountVoucherResponse> {
  return request.post<CreateMetasearchDiscountVoucherResponse, CreateMetasearchDiscountVoucherInput>(
    '/api/connections/google/discount-voucher',
    input,
    {
      ...authOptions(),
      timeout: METASEARCH_VOUCHER_API_TIMEOUT_MS,
    },
  ).then(response => ({
    voucherId: response?.voucherId,
    applyPublicRate: response?.applyPublicRate === true ? true as const : undefined,
  })).catch(() => ({
    voucherId: undefined,
  }))
}
